import { useState, useEffect } from 'react';

/**
 * useNetworkStatus
 *
 * Monitors the device's network connectivity using the browser's
 * `online` / `offline` events. Works on web, iOS and Android via Capacitor
 * (Capacitor bridges these events from the native layer automatically).
 *
 * @returns { isOnline } — true when connected, false when offline.
 */
export function useNetworkStatus(): { isOnline: boolean } {
    const [isOnline, setIsOnline] = useState<boolean>(
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return { isOnline };
}
